import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PhoneWrapper } from 'src/svg/phone-wrapper';
import { AndroidPhoneWrapper } from 'src/svg/android-phone-wrapper';
import { FontLoader } from 'src/components/font-loader';

import { isBackgroundTooDark } from '@dutchie/common/utils/background-contrast';
import { MobileNavigationStyle } from 'types/graphql';
import { usePreview } from './use-preview';
import { PreviewData } from './preview.types';
import TabBar from './mobile/mobile-tab-bar';
import MobileHeader from './mobile/mobile-header';

export type PreviewProps = {
  asMobileApp?: boolean;
  children: React.ReactNode;
  data: PreviewData;
  mobilePlatform?: 'android' | 'ios';
};

export function Preview({ asMobileApp, children, data, mobilePlatform = 'ios' }: PreviewProps): JSX.Element {
  const { theme, encodedFonts } = usePreview(data);
  const flags = useFlags();
  const showMobileRedesign = flags['ecomm.app.v2-redesign'] ?? false;
  const advancedCustomizationEnabled = flags['ecomm.app.advanced-customization'] ?? false;
  const isLiquidGlass =
    (asMobileApp ?? false) &&
    advancedCustomizationEnabled &&
    data.mobile?.bottomBarStyle !== MobileNavigationStyle.solid;
  // Only the mobile app surface actually renders the configured background
  // color (see ContentContainer below) — the web previews below always sit
  // on white, so this only ever flips text to dark-mode colors when the
  // background it's rendered against is the dark one that triggered it.
  const mobileAppTheme = {
    ...theme,
    isBackgroundDark: (asMobileApp ?? false) && isBackgroundTooDark(data.mobile?.backgroundColor),
  };

  return (
    <ThemeProvider theme={mobileAppTheme}>
      <FontLoader encodedFonts={encodedFonts} />

      <Container>
        {mobilePlatform === 'android' ? <AndroidPhoneWrapper data-testid='android-device-frame' /> : <PhoneWrapper />}

        <ContentContainer $backgroundColor={asMobileApp ?? false ? data.mobile?.backgroundColor : null}>
          {!showMobileRedesign && asMobileApp && (
            <MobileHeader
              backgroundColor={data.mobile?.headerBackgroundColor}
              logo={data.mobile?.headerImageUrl}
              navigationStyle={data.mobile?.headerNavigationStyle}
            />
          )}
          {!asMobileApp && <PreviewBanner>Preview</PreviewBanner>}
          <ScrollableContainer
            data-testid='preview-scroll-container'
            $asMobileApp={asMobileApp ?? false}
            $isLiquidGlass={isLiquidGlass}
          >
            {showMobileRedesign && asMobileApp && (
              <MobileHeader
                backgroundColor={data.mobile?.headerBackgroundColor}
                logo={data.mobile?.headerImageUrlV2}
                navigationStyle={data.mobile?.headerNavigationStyle}
              />
            )}
            {children}
          </ScrollableContainer>
          {asMobileApp && <TabBar mobileCustomization={data.mobile} />}
        </ContentContainer>
      </Container>
    </ThemeProvider>
  );
}

const Container = styled.div`
  height: 802px;
  max-width: 370px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const ScrollableContainer = styled.div<{ $asMobileApp: boolean; $isLiquidGlass: boolean }>`
  flex: ${({ $asMobileApp, $isLiquidGlass }) => ($asMobileApp && !$isLiquidGlass ? '1 1 auto' : 'none')};
  font-family: ${({ theme }) => theme.primaryFont};
  height: ${({ $asMobileApp, $isLiquidGlass }) => {
    if ($isLiquidGlass) return '100%';
    return $asMobileApp ? 'auto' : '720px';
  }};
  min-height: 0;
  margin-top: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const ContentContainer = styled.div<{ $backgroundColor?: string | null }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor ?? '#FFFFFF'};
  border-radius: 44px;
  display: flex;
  flex-direction: column;
  bottom: 12px;
  left: 11px;
  overflow: hidden;
  position: absolute;
  right: 11px;
  top: 11px;
  // Old school fix for Safari not respecting border radius clipping
  transform: translate3d(0, 0, 0);

  & a {
    color: ${({ theme }) => theme.linkColor};
  }
`;

const PreviewBanner = styled.div`
  font-size: 12px;
  font-weight: 700;
  height: 60px;
  line-height: ${17 / 12};
  padding: 33px 0 0;
  text-align: center;
  text-transform: uppercase;
`;
